import { useCallback, useEffect, useState } from 'react';
import { useWebSocket } from '@/shared/hooks';
import { jobsService } from '../services/jobs.service';
import type { Job, JobWebSocketMessage, JobStatus } from '../types/jobs.types';

interface UseJobWebSocketReturn {
  status: JobStatus;
  progress: number;
  completed: number;
  total: number;
  isConnected: boolean;
  error?: string;
}

export function useJobWebSocket(
  jobId: string | null,
  initialJob?: Job
): UseJobWebSocketReturn {
  const [jobState, setJobState] = useState<UseJobWebSocketReturn>({
    status: initialJob?.status || 'pending',
    progress: initialJob?.progress || 0,
    completed: initialJob?.completed || 0,
    total: initialJob?.total || 0,
    isConnected: false,
    error: undefined,
  });

  const wsUrl = jobId ? jobsService.getWebSocketUrl(jobId) : null;

  const handleMessage = useCallback((data: unknown) => {
    const message = data as JobWebSocketMessage;
    setJobState((prev) => ({
      ...prev,
      status: message.status,
      progress: message.progress,
      completed: message.completed,
      total: message.total,
      error: message.error,
    }));
  }, []);

  const { status: wsStatus } = useWebSocket<JobWebSocketMessage>(wsUrl, {
    onMessage: handleMessage,
    onOpen: () => setJobState((prev) => ({ ...prev, isConnected: true })),
    onClose: () => setJobState((prev) => ({ ...prev, isConnected: false })),
    reconnect: true,
    maxReconnectAttempts: 10,
  });

  // Update connection status
  useEffect(() => {
    setJobState((prev) => ({
      ...prev,
      isConnected: wsStatus === 'connected',
    }));
  }, [wsStatus]);

  // Update from initial job if provided
  useEffect(() => {
    if (initialJob) {
      setJobState((prev) => ({
        ...prev,
        status: initialJob.status,
        progress: initialJob.progress,
        completed: initialJob.completed,
        total: initialJob.total,
      }));
    }
  }, [initialJob]);

  return jobState;
}
